/**
 * <pd-quiz> — FOI-310 product quiz behaviour.
 *
 * Contracts this file depends on (do not rename hooks without updating both):
 *   markup  src/snippets/pd-quiz.liquid        — every stage + the results panel
 *                                                are server-rendered on first paint
 *   config  src/snippets/pd-quiz-config.liquid — <script id="pd-quiz-config">
 *   scoring src/assets/pd-quiz-rules.js + src/assets/pd-quiz-engine.js
 *
 * Variant IDs are NEVER hardcoded. Kit variants are resolved by matching option
 * VALUES (battery / motor / AC-lead strings from the config) because the same
 * kit carries different variant IDs on the AU, EU and US stores.
 *
 * Degradation: if the config, the rules or the engine are missing the quiz
 * still navigates and the results panel still opens — it just drops every hook
 * that would render empty and keeps Re-Take plus the "Need some advice?" band.
 * The shopper is never stuck on a dead stage.
 */
if (!customElements.get('pd-quiz')) {
  customElements.define(
    'pd-quiz',
    class PdQuiz extends HTMLElement {
      constructor() {
        super();

        this.storageKey = 'pd-quiz-state-v1';
        this.emailSubject = 'My Foil Drive kit recommendation';

        this.stages = Array.from(this.querySelectorAll('[data-quiz-stage]'));
        this.liveRegion = this.querySelector('[data-quiz-live]');
        this.results = this.querySelector('[data-quiz-results]');
        this.resultsBand = this.querySelector('.pd-quiz__results-band');
        this.resultHero = this.querySelector('.pd-quiz__result-hero');
        this.upgradesContainer = this.querySelector('[data-result-upgrades]');
        this.upgradeTemplate = this.querySelector('[data-upgrade-card-template]');
        this.acLeadFieldset = this.querySelector('[data-ac-lead]');
        this.notice = this.querySelector('[data-oos-notice]');
        this.kitAtc = this.querySelector('[data-result-kit-atc]');
        this.kitAltLink = this.querySelector('[data-result-kit-alt-link]');
        this.kitPrice = this.querySelector('[data-result-kit-price]');
        this.kitCompare = this.querySelector('[data-result-kit-compare]');
        this.addAllButton = this.querySelector('[data-result-add-all]');
        this.addAllLabel = this.addAllButton ? this.addAllButton.textContent.trim() : '';
        this.kitAtcLabel = this.kitAtc ? this.kitAtc.textContent.trim() : '';
        this.exploreLink = this.querySelector('[data-result-explore]');
        this.emailKitLink = this.querySelector('[data-email-kit]');

        // Advice cards are merchant-authored blocks, so their links are whatever
        // the merchant typed. Only the mailto: ones get the kit appended; a card
        // pointing at /pages/dealers is left exactly as rendered.
        this.adviceMailtoLinks = Array.from(this.querySelectorAll('[data-advice-card]')).filter(
          (link) => (link.getAttribute('href') || '').indexOf('mailto:') === 0
        );

        // The Liquid already renders a recipient on these links; keep it and only
        // append the subject/body we build at results time.
        this.emailKitBase = this.mailtoBase(this.emailKitLink);
        this.adviceMailtoBases = this.adviceMailtoLinks.map((link) => this.mailtoBase(link));

        this.config = this.readConfig();

        this.stageIndex = 0;
        this.transitioning = false;
        this.started = false;
        this.result = null;
        // The engine's recommendation as one flat view — see recommendedBuild().
        this.build = null;
        this.kitProduct = null;
        this.kitVariant = null;
        this.kitPurchasable = false;
        this.batteryKey = null;
        this.upgradeItems = [];

        this.onChange = this.handleChange.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.onHoverIn = this.handleHoverIn.bind(this);
        this.onHoverOut = this.handleHoverOut.bind(this);
        // Safari paints :focus-visible on programmatic focus; track modality
        // so pointer-driven stage changes don't show a focus ring (keyboard
        // users keep it). See pd-bug-patterns: safari-focus-visible-on-programmatic-focus.
        this.pointerModality = false;
        this.onPointerDown = () => { this.pointerModality = true; };
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        // pointerenter/pointerleave do not bubble, so delegate in the capture phase.
        this.addEventListener('pointerenter', this.onHoverIn, true);
        this.addEventListener('pointerleave', this.onHoverOut, true);
        this.addEventListener('focusin', this.onHoverIn);
        this.addEventListener('focusout', this.onHoverOut);
        this.addEventListener('pointerdown', this.onPointerDown, true);
        this.addEventListener('keydown', this.onKeyDown, true);

        // Theme editor only. The advice cards sit inside [data-quiz-results],
        // which is `hidden` until the shopper finishes the quiz, so selecting a
        // card in the editor would scroll to nothing. Open the results panel for
        // the merchant instead. shopify:block:select fires on `document`, not on
        // this element, so it cannot use the delegation above.
        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }

        this.restore();
        if (this.stages.length) this.syncStage(this.stages[this.stageIndex]);
        this.watchHeroBand();
      }

      /**
       * QC #154 — below tablet the background media has to stop under the
       * "WE'D RECOMMEND" header instead of running the full height of the
       * results band. The CSS does the clipping, but the header's height is
       * whatever the kit name wraps to, so only a measurement knows it.
       *
       * Publishes --pd-quiz-hero-band-height on this element. Desktop ignores
       * the variable entirely — the hero is a sticky column there and the media
       * is meant to cover the whole band.
       */
      syncHeroBandHeight() {
        if (!this.resultsBand || !this.resultHero) return;
        const band = this.resultsBand.getBoundingClientRect();
        const hero = this.resultHero.getBoundingClientRect();
        // A hidden results panel measures 0; leave the last good value alone
        // rather than writing a 0 the CSS would clip everything to.
        if (!hero.height) return;
        // Figma 7448:29574 puts Spacing/64 under the kit name before the white
        // panel starts. The band's own top padding is already inside this
        // measurement because it runs from the band's top edge.
        const height = Math.round(hero.bottom - band.top + 64);
        this.style.setProperty('--pd-quiz-hero-band-height', height + 'px');
      }

      watchHeroBand() {
        if (!this.resultHero) return;
        this.syncHeroBandHeight();
        if (typeof window.ResizeObserver !== 'function') {
          // No observer: a resize listener still covers rotation and font swaps.
          this.onHeroResize = () => this.syncHeroBandHeight();
          window.addEventListener('resize', this.onHeroResize);
          return;
        }
        this.heroObserver = new ResizeObserver(() => this.syncHeroBandHeight());
        this.heroObserver.observe(this.resultHero);
        if (this.resultsBand) this.heroObserver.observe(this.resultsBand);
      }

      disconnectedCallback() {
        if (this.heroObserver) {
          this.heroObserver.disconnect();
          this.heroObserver = null;
        }
        if (this.onHeroResize) {
          window.removeEventListener('resize', this.onHeroResize);
          this.onHeroResize = null;
        }
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('pointerenter', this.onHoverIn, true);
        this.removeEventListener('pointerleave', this.onHoverOut, true);
        this.removeEventListener('focusin', this.onHoverIn);
        this.removeEventListener('focusout', this.onHoverOut);
        this.removeEventListener('pointerdown', this.onPointerDown, true);
        this.removeEventListener('keydown', this.onKeyDown, true);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      /**
       * Keyboard modality, plus Enter on an option.
       *
       * QC a11y round 2 — an option could only be picked with Space. That is
       * the browser's own behaviour and there is no CSS for it: Space is the
       * activation key for a checkbox or a radio, while Enter is reserved for
       * submitting the form the control sits in. The quiz renders its stages in
       * a <fieldset> with no <form> around it, so Enter did nothing at all —
       * the shopper pressed it, nothing moved, and the page gave no reason why.
       *
       * Enter is added rather than swapped in. Space keeps working, so nobody
       * who already knows the native key loses it.
       *
       * preventDefault() matters even with no <form> here: a theme that later
       * wraps the section in one would start submitting the page on Enter, and
       * on a checkbox Enter can also scroll. Setting `.checked` by hand does
       * NOT fire `change` on its own, so the event is dispatched — bubbling, so
       * handleChange() picks it up through the same delegation as a real click.
       */
      handleKeyDown(event) {
        this.pointerModality = false;
        if (event.key !== 'Enter' || event.altKey || event.ctrlKey || event.metaKey) return;

        const input = event.target;
        if (!input || !input.hasAttribute || !input.hasAttribute('data-quiz-input')) return;

        event.preventDefault();
        // A radio cannot be unchecked by its own key — picking it again is a
        // no-op, the same as Space. A checkbox toggles, because the multi-select
        // stages have to be able to drop an answer.
        if (input.type === 'checkbox') {
          input.checked = !input.checked;
        } else {
          if (input.checked) return;
          input.checked = true;
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      handleBlockSelect(event) {
        const block = event.target;
        if (!block || !this.contains(block) || !block.hasAttribute('data-advice-card')) return;
        this.stages.forEach((stage) => this.hide(stage));
        this.show(this.results);
        this.scrollElementIntoView(block);
      }

      // Move focus for keyboard users without painting a ring after
      // pointer-driven navigation (Safari always matches :focus-visible on
      // programmatic focus; the aria-live announcement covers the rest).
      moveFocus(el) {
        if (!el) return;
        el.focus({ preventScroll: true });
        if (this.pointerModality) el.blur();
      }

      /* ---------------------------------------------------------------- setup */

      readConfig() {
        const node = this.querySelector('#pd-quiz-config');
        if (!node) {
          console.warn('[pd-quiz] missing config');
          return null;
        }
        try {
          return JSON.parse(node.textContent);
        } catch (err) {
          console.warn('[pd-quiz] missing config');
          return null;
        }
      }

      get rules() {
        return window.PD_QUIZ_RULES || null;
      }

      canScore() {
        return !!(this.config && window.PdQuizEngine && this.rules);
      }

      /* ------------------------------------------------------------- utilities */

      show(el) {
        if (el) el.removeAttribute('hidden');
      }

      hide(el) {
        if (el) el.setAttribute('hidden', '');
      }

      toggle(el, visible) {
        if (visible) this.show(el);
        else this.hide(el);
      }

      announce(message) {
        if (this.liveRegion) this.liveRegion.textContent = message;
      }

      /**
       * QC #136 — the results HERO shows the product wordmark where Figma draws
       * one, and falls back to the kit name where it does not.
       *
       * The hero, not the kit card. Figma 7437:23095 — the card's product title
       * — is a text node, and the only "Product logos" component in the file is
       * a WHITE wordmark, which would be invisible on the card's white panel.
       * The hero sits on the background photo, which is what white is for.
       *
       * Scoped to .pd-quiz__result-hero-name on purpose: [data-result-kit-name]
       * also marks the Explore button's label, and that one must stay text.
       *
       * Keyed by SYSTEM rather than by product, so the logo survives the product
       * being swapped from the test SKU to the real 2027 one. The <img> is built
       * with createElement, not innerHTML: the URL comes from a merchant setting
       * and the name from the rules file, so neither is written as markup. The
       * logo carries the kit name as its alt text, so the hero reads the same to
       * a screen reader either way.
       */
      renderHeroName(system, name) {
        const el = this.querySelector('.pd-quiz__result-hero-name [data-result-kit-name]');
        if (!el) return;
        const src = ((this.config && this.config.logos) || {})[system];
        if (!src) {
          el.textContent = name;
          return;
        }
        el.textContent = '';
        const img = document.createElement('img');
        img.className = 'pd-quiz__result-hero-logo';
        img.src = src;
        img.alt = name;
        // Not lazy: it is the first thing on the results screen, above the fold.
        img.addEventListener('error', () => { el.textContent = name; }, { once: true });
        el.appendChild(img);
      }

      setText(selector, value) {
        this.querySelectorAll(selector).forEach((el) => {
          el.textContent = value;
        });
      }

      track(payload) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
      }

      displayName(key) {
        const names = (this.rules && this.rules.displayNames) || {};
        return names[key] || key;
      }

      // The battery name the shopper actually buys: the store's variant
      // option string (per-store, from pd-quiz-config), falling back to the
      // workbook display name when no mapping exists.
      batteryOptionName(batteryKey) {
        const map = (this.config && this.config.optionNames && this.config.optionNames.battery) || {};
        return map[batteryKey] || this.displayName(batteryKey);
      }

      // Design shows "$6,600.00 AUD". request.locale.iso_code is usually plain
      // "en", which renders AUD as "A$" — narrowSymbol keeps the bare "$" and
      // the ISO code we append carries the disambiguation.
      formatMoney(cents) {
        const config = this.config || {};
        const amount = (Number(cents) || 0) / 100;
        const locale = config.locale || undefined;
        let formatted;
        try {
          formatted = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: config.currency,
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (err) {
          try {
            formatted = new Intl.NumberFormat(locale, { style: 'currency', currency: config.currency }).format(amount);
          } catch (fallbackError) {
            formatted = amount.toFixed(2);
          }
        }
        return config.currency ? `${formatted} ${config.currency}` : formatted;
      }

      // Every shopper-facing price goes through here, never through textContent
      // directly. Two jobs:
      //
      //  1. Write the quiz's own formatted price, so a store with no tax rate
      //     (or before main.js has resolved the visitor country) still shows a
      //     correct number.
      //  2. Hand the price to the theme's tax layer. updatePricesWithTaxes()
      //     in main.js only rewrites elements carrying data-product-grid-item-price,
      //     and it reads RAW CENTS from that attribute (see price.liquid). Without
      //     it the quiz shows an ex-tax price while the product page and the cart
      //     drawer show an inc-tax one — a 10% gap on AU, 19-25% across the EU.
      //
      // priceOriginalHtml is main.js's stash of the markup it first overwrote,
      // used to put an untaxed price back. Our elements change price on AC-lead
      // switches and re-runs of the quiz, so a stale stash would restore the
      // PREVIOUS recommendation's price. Clear it on every write.
      setPrice(el, cents) {
        if (!el) return;
        el.textContent = this.formatMoney(cents);
        el.dataset.productGridItemPrice = String(Number(cents) || 0);
        delete el.dataset.priceOriginalHtml;
      }

      clearPrice(el) {
        if (!el) return;
        el.textContent = '';
        delete el.dataset.productGridItemPrice;
        delete el.dataset.priceOriginalHtml;
      }

      // main.js listens for this on document and re-runs the tax pass. Fired
      // once per results render, after every price on the page has been written.
      notifyPricesRendered() {
        document.dispatchEvent(new Event('recommendations:loaded'));
      }

      mailtoBase(link) {
        const href = link ? link.getAttribute('href') || '' : '';
        return href.indexOf('mailto:') === 0 ? href.split('?')[0] : 'mailto:';
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /**
       * QC #138 — `scrollIntoView({ block: 'start' })` parks the stage's top at
       * viewport 0, which is UNDER the sticky header. Worse, scrolling to a
       * higher position is a scroll UP, and that is exactly the gesture the
       * "show on scroll up" header listens for: it slides in and covers the
       * progress label and the question, so the first thing a shopper sees is
       * the option list and they have to scroll back up to read the question.
       *
       * Scroll by hand instead, reserving the header's height. Same expression
       * .pd-quiz__result-hero already uses for its sticky `top`, so the two
       * cannot drift apart.
       */
      stickyOffset() {
        const cs = window.getComputedStyle(document.documentElement);
        const px = (name) => parseFloat(cs.getPropertyValue(name)) || 0;
        // Only ONE announcement-bar variable is ever non-zero — the merchant
        // picks sticky or show-on-scroll-up — so take whichever is set.
        const bar = Math.max(px('--annoucement-bar-height'), px('--annoucement-bar-height-scroll-up'));
        return bar + px('--header-height') + px('--secondary-header-height') + 24;
      }

      scrollElementIntoView(el) {
        if (!el || typeof el.getBoundingClientRect !== 'function') return;
        const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
        if (typeof window.scrollTo !== 'function') {
          if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior, block: 'start' });
          return;
        }
        const top = el.getBoundingClientRect().top + window.scrollY - this.stickyOffset();
        window.scrollTo({ top: Math.max(0, top), behavior });
      }

      /* -------------------------------------------------------------- stages */

      handleChange(event) {
        const target = event.target;
        if (!target) return;

        if (target.name === 'ac_lead') {
          if (!this.result) return;
          // The whole results panel is re-rendered, not just the kit card.
          // Stock is read per AC lead (see kitVariantFor), so switching the
          // lead can turn Sold Out into Add To Cart, and the total and Add All
          // have to follow.
          this.renderResults(this.result);
          return;
        }

        if (!target.hasAttribute('data-quiz-input')) return;

        if (!this.started) {
          this.started = true;
          this.track({ event: 'quiz_start' });
        }

        const stage = target.closest('[data-quiz-stage]');
        this.syncStage(stage);
        this.persist();
      }

      handleClick(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        if (target.closest('[data-quiz-next]')) {
          this.goNext();
          return;
        }
        if (target.closest('[data-quiz-back]')) {
          this.goBack();
          return;
        }
        if (target.closest('[data-retake]')) {
          this.retake();
          return;
        }
        const kitButton = target.closest('[data-result-kit-atc]');
        if (kitButton) {
          this.addKitToCart(kitButton);
          return;
        }
        const upgradeButton = target.closest('[data-upgrade-atc]');
        if (upgradeButton) {
          this.addUpgradeToCart(upgradeButton);
          return;
        }
        if (target.closest('[data-result-add-all]')) {
          this.addAllToCart();
        }
      }

      hasAnswer(stage) {
        return !!(stage && stage.querySelectorAll('[data-quiz-input]:checked').length);
      }

      // Enable/disable Next, swap its label, and apply the multi-select cap for
      // one stage. One function owns all three so they can never disagree.
      syncStage(stage) {
        if (!stage) return;

        const answered = this.hasAnswer(stage);
        const next = stage.querySelector('[data-quiz-next]');
        if (next) {
          next.disabled = !answered;
          // FOI-537 #112. Both strings come from data- attributes the Liquid
          // renders (src/snippets/pd-quiz.liquid) — never hardcode shopper-facing
          // English here. A button missing the attributes keeps its markup label.
          const label = answered ? next.dataset.labelReady : next.dataset.labelEmpty;
          if (label && next.textContent.trim() !== label) next.textContent = label;
        }

        const max = parseInt(stage.dataset.max, 10);
        if (!max) return;

        const inputs = Array.from(stage.querySelectorAll('[data-quiz-input]'));
        const checked = inputs.filter((input) => input.checked);
        const wasCapped = inputs.some((input) => !input.checked && input.disabled);
        const isCapped = checked.length >= max;

        inputs.forEach((input) => {
          input.disabled = isCapped && !input.checked;
        });

        if (isCapped && !wasCapped) this.announce(`You can pick up to ${max}`);
      }

      showStage(index, options) {
        if (!this.stages.length) return;
        const settings = options || {};
        const clamped = Math.max(0, Math.min(index, this.stages.length - 1));

        this.stages.forEach((stage, i) => this.toggle(stage, i === clamped));
        this.stageIndex = clamped;

        const stage = this.stages[clamped];
        this.syncStage(stage);
        if (settings.silent) return;

        this.announce(`Question ${clamped + 1} of ${this.stages.length}`);
        this.moveFocus(stage.querySelector('legend'));
        this.scrollElementIntoView(stage);
      }

      goNext() {
        if (this.transitioning) return;
        const stage = this.stages[this.stageIndex];
        if (!this.hasAnswer(stage)) return;

        this.transitioning = true;
        window.requestAnimationFrame(() => {
          this.transitioning = false;
        });

        this.trackStageAnswer(stage);

        if (this.stageIndex === this.stages.length - 1) {
          this.submit();
          return;
        }
        this.showStage(this.stageIndex + 1);
        this.persist();
      }

      goBack() {
        if (this.transitioning) return;
        if (this.stageIndex === 0) return;

        this.transitioning = true;
        window.requestAnimationFrame(() => {
          this.transitioning = false;
        });

        this.showStage(this.stageIndex - 1);
        this.persist();
      }

      trackStageAnswer(stage) {
        if (!stage) return;
        const key = stage.dataset.question;
        const values = Array.from(stage.querySelectorAll('[data-quiz-input]:checked')).map((i) => i.value);
        this.track({
          event: 'quiz_question_answered',
          quiz_question: key,
          quiz_answer: stage.dataset.select === 'multi' ? values : values[0],
          quiz_stage: this.stages.indexOf(stage) + 1,
        });
      }

      collectAnswers() {
        const answers = {};
        this.stages.forEach((stage) => {
          const key = stage.dataset.question;
          if (!key) return;
          const values = Array.from(stage.querySelectorAll('[data-quiz-input]:checked')).map((i) => i.value);
          if (stage.dataset.select === 'multi') answers[key] = values;
          else if (values.length) answers[key] = values[0];
        });
        return answers;
      }

      /* ------------------------------------------------------------- storage */

      // `completed` is what makes the browser Back button land on the results
      // panel instead of the last question (FOI-537 #110). The stash used to hold
      // a stage number and nothing else, so "finished" and "sitting on question
      // 7" looked identical on the way back in.
      //
      // Every caller except submit() is a shopper still working through the
      // questions, so the flag defaults to false — changing an answer or moving
      // a stage clears it on its own.
      persist(options) {
        const settings = options || {};
        try {
          sessionStorage.setItem(
            this.storageKey,
            JSON.stringify({
              answers: this.collectAnswers(),
              stage: this.stageIndex + 1,
              completed: !!settings.completed,
            })
          );
        } catch (err) {
          // sessionStorage can be unavailable (private mode); the quiz still works.
        }
      }

      clearStorage() {
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch (err) {
          // nothing to clean up
        }
      }

      restore() {
        let saved = null;
        try {
          const raw = sessionStorage.getItem(this.storageKey);
          saved = raw ? JSON.parse(raw) : null;
        } catch (err) {
          this.clearStorage();
          return;
        }
        if (!saved || typeof saved !== 'object') return;

        const answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : {};
        this.stages.forEach((stage) => {
          const value = answers[stage.dataset.question];
          const values = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
          stage.querySelectorAll('[data-quiz-input]').forEach((input) => {
            input.checked = values.indexOf(input.value) !== -1;
          });
        });

        const stage = parseInt(saved.stage, 10);
        this.showStage(stage >= 1 ? stage - 1 : 0, { silent: true });

        // FOI-537 #110 — a shopper who finished the quiz, opened a product, then
        // pressed Back must come back to the results, not to question 7.
        // Re-scoring here is safe: pd-quiz-rules.js and pd-quiz-engine.js are
        // plain `defer` scripts loaded BEFORE this one, so both are already on
        // `window` by the time connectedCallback runs.
        if (saved.completed) this.submit({ restored: true });
      }

      /* ------------------------------------------------------------- results */

      /**
       * Open the results panel.
       *
       * options.restored — the page was re-entered with a finished quiz in the
       * stash (FOI-537 #110), not pressed by a shopper. Re-showing what they
       * already saw must not fire quiz_completed a second time, and must not
       * steal focus or scroll away from the position the browser just restored.
       */
      submit(options) {
        const settings = options || {};
        let result = null;
        if (this.canScore()) {
          try {
            result = window.PdQuizEngine.computeRecommendation(this.collectAnswers(), this.rules);
          } catch (err) {
            // The stage-level validation makes this unreachable; never strand the shopper.
            console.warn('[pd-quiz] could not score these answers', err);
            // A stash that cannot be scored would otherwise re-enter this branch
            // on every page load in the session, leaving the shopper on the last
            // question with a button that does nothing. Drop it so the next load
            // starts a clean quiz.
            if (settings.restored) this.clearStorage();
            return;
          }
        }

        this.stages.forEach((stage) => this.hide(stage));
        this.show(this.results);
        this.resetResultsVisibility();
        // From here on the stash says "finished", so the next restore() opens
        // this panel instead of the last question. Re-Take still wipes it.
        this.persist({ completed: true });

        if (!result) {
          this.renderAdviceOnly();
          if (!settings.restored) this.focusResults();
          return;
        }

        this.result = result;
        this.renderResults(result);
        if (!settings.restored) this.focusResults();
        if (settings.restored) return;

        // Report the build the shopper was shown. It is the engine's
        // recommendation: stock never changes it — a sold-out build shows a
        // disabled Sold Out button instead of another product.
        const build = this.build || {};
        this.track({
          event: 'quiz_completed',
          quiz_system: build.system || result.system,
          quiz_battery: build.battery || result.battery,
          quiz_motor: build.motor || result.motor,
          quiz_motor_height: result.motorHeight || '',
          quiz_cables: (build.cables || []).map((cable) => cable.order),
          quiz_accessories: result.accessories || [],
          quiz_integrated_mast: !!build.integrated,
        });
      }

      get resultBlocks() {
        return ['.pd-quiz__result-hero', '.pd-quiz__kit', '.pd-quiz__upgrades-panel', '.pd-quiz__totals'];
      }

      resetResultsVisibility() {
        this.resultBlocks.forEach((selector) => this.show(this.querySelector(selector)));
        this.show(this.exploreLink);
        this.show(this.emailKitLink);
      }

      // No config / no engine: keep Re-Take and the advice band, drop everything
      // that would render empty. The advice band is not in resultBlocks, so it
      // survives this path without a line of its own.
      renderAdviceOnly() {
        this.resultBlocks.forEach((selector) => this.hide(this.querySelector(selector)));
        this.hide(this.exploreLink);
        this.hide(this.emailKitLink);
      }

      focusResults() {
        if (!this.results) return;
        const heading =
          Array.from(this.results.querySelectorAll('h2')).filter((h) => !h.closest('[hidden]'))[0] || this.results;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        this.moveFocus(heading);
        this.scrollElementIntoView(this.results);
      }

      // The AC lead is read while looking up the kit variant, so it has to be
      // settled before renderKit() runs. Everything works off `build`, one flat
      // view of the engine result, so the kit card, the cable cards and the
      // mode gating cannot disagree about which build is on screen.
      renderResults(result) {
        this.setupAcLead();
        this.build = this.recommendedBuild(result);
        this.renderUpgrades(result, this.build);
        this.renderKit(result, this.build);
        this.applyModeGating(this.build.integrated);
        this.updateTotals();
        this.updateLinks(this.build);
      }

      applyModeGating(integrated) {
        this.querySelectorAll('[data-integrated-only]').forEach((el) => this.toggle(el, integrated));
        this.querySelectorAll('[data-standard-only]').forEach((el) => this.toggle(el, !integrated));

        const links = (this.config && this.config.links) || {};
        const rangeLink = this.querySelector('[data-integrated-range-link]');
        if (rangeLink && links.integratedMastRange) rangeLink.href = links.integratedMastRange;
      }

      setupAcLead() {
        if (!this.acLeadFieldset) return;
        if (!this.config || !this.config.hasAcLead) {
          this.hide(this.acLeadFieldset);
          return;
        }
        const fallback = this.config.acLeadDefault || '';
        const checked = this.acLeadFieldset.querySelector('input[name="ac_lead"]:checked');
        if (!checked && fallback) {
          const preferred = this.acLeadFieldset.querySelector(`input[name="ac_lead"][value="${fallback}"]`);
          if (preferred) preferred.checked = true;
        }
        this.show(this.acLeadFieldset);
      }

      getAcLead() {
        if (!this.config || !this.config.hasAcLead) return '';
        const checked = this.querySelector('[data-ac-lead] input[name="ac_lead"]:checked');
        return checked ? checked.value : this.config.acLeadDefault || '';
      }

      /* --------------------------------------------------- variant resolution */

      product(key) {
        const products = (this.config && this.config.products) || {};
        return products[key] || null;
      }

      optionValues(variant) {
        return (variant.options || []).map((option) => String(option == null ? '' : option).trim());
      }

      productHasOptionValue(product, value) {
        const wanted = String(value).trim();
        return (product.variants || []).some((variant) => this.optionValues(variant).indexOf(wanted) !== -1);
      }

      matchVariants(product, values) {
        const wanted = values.filter(Boolean).map((value) => String(value).trim());
        return (product.variants || []).filter((variant) => {
          const options = this.optionValues(variant);
          return wanted.every((value) => options.indexOf(value) !== -1);
        });
      }

      // The store's option value for one component key, or '' when this store
      // does not sell it. '' is meaningful: it is how pd-quiz-config says
      // "absent from this catalogue" (e.g. FM+ 430 before the December launch).
      optionValueFor(componentName, key) {
        const names = ((this.config && this.config.optionNames) || {})[componentName] || {};
        const value = names[key];
        return value ? String(value).trim() : '';
      }

      // The build to show, as one flat view for the renderers:
      //   { system, battery, motor, integrated, cables }
      // It is exactly what the engine recommended. Stock never changes it: when
      // the recommended build has no buyable variant the kit card shows a
      // disabled Sold Out button (renderKit). It does not swap in another kit,
      // battery or motor.
      recommendedBuild(result) {
        return {
          system: result.system,
          battery: result.battery,
          motor: result.motor,
          integrated: !!(result.flags && result.flags.integratedMast),
          cables: result.cables || [],
        };
      }

      // Does this kit list the recommended motor as an option value at all?
      kitHasMotorOption(product, motorKey) {
        const value = this.optionValueFor('motor', motorKey);
        return !!(product && value && this.productHasOptionValue(product, value));
      }

      // The kit variant carrying BOTH option values plus the selected AC lead.
      // A buyable row wins; otherwise the first matching row, so a sold-out
      // build can still show its price. null when no row carries the
      // combination at all — FM+ 430 before its December launch, or an AC lead
      // this kit does not sell. `available` on the returned variant is what
      // tells Add To Cart apart from Sold Out (see renderKit).
      kitVariantFor(product, batteryKey, motorKey) {
        const batteryValue = this.optionValueFor('battery', batteryKey);
        const motorValue = this.optionValueFor('motor', motorKey);
        if (!product || !batteryValue || !motorValue) return null;
        const matches = this.matchVariants(product, [batteryValue, motorValue, this.getAcLead()]);
        return matches.filter((variant) => variant.available !== false)[0] || matches[0] || null;
      }

      // The kit variant for the recommended build, buyable or not, or null.
      resolveKitVariant(product, build) {
        return this.kitVariantFor(product, build.battery, build.motor);
      }

      /* ------------------------------------------------------------ kit card */

      renderKit(result, build) {
        const product = this.product(build.system);
        this.kitProduct = product;
        this.kitVariant = null;
        this.kitPurchasable = false;
        this.batteryKey = build.battery;

        this.showNotice('');

        // FOI-540 QC bug 3. The kit NAME is written before the early return
        // below, because both [data-result-kit-name] hooks sit OUTSIDE the kit
        // card that the return hides: the hero band ("WE'D RECOMMEND …", which
        // resetResultsVisibility always shows) and the Explore button's label.
        // build.system is known with or without a product, so the hero can
        // always name the kit. Writing it after the return left the hero as a
        // label above a blank line on EVERY run before 23 Sept, while the three
        // 2027 kits are still DRAFT and every product resolves to null.
        this.setText('[data-result-kit-name]', this.displayName(build.system));
        this.renderHeroName(build.system, this.displayName(build.system));

        if (!product) {
          console.warn(`[pd-quiz] no product configured for "${build.system}"`);
          this.hide(this.querySelector('.pd-quiz__kit'));
          // Nothing to explore: updateLinks() only sets the href from a product,
          // so the link would keep the markup default "#" — a dead control.
          this.hide(this.exploreLink);
          return;
        }
        // A previous render (or a previous AC lead) may have hidden the card.
        this.show(this.querySelector('.pd-quiz__kit'));
        this.show(this.exploreLink);

        // FOI-540. This used to read `integrated && result.system !== 'Fusion'`,
        // on the belief that only the Fusion kit had an integrated-mast variant.
        // The AU catalogue read of 2026-08-22 disproves it: all three 2027 kits
        // carry "Integrated Mast High Power Motor". MAX+ and Slim shoppers on
        // the integrated path were being sent to a browse link instead of Add To
        // Cart. Ask the resolved kit what it actually lists, and nothing else —
        // a store whose kit really has no integrated variant still gets the link.
        const integrated = build.integrated;
        const integratedWithoutKit = integrated && !this.kitHasMotorOption(product, build.motor);

        if (!integratedWithoutKit) {
          this.kitVariant = this.resolveKitVariant(product, build);
          this.kitPurchasable = !!(this.kitVariant && this.kitVariant.available !== false);
        }

        // The kit NAME is already written, above the early return. The card's
        // title is TEXT in Figma (frame 7437:23095 is a text node, not a logo
        // instance) — the wordmark belongs to the hero, see renderHeroName.
        this.setText('[data-result-kit-title]', product.title || '');
        // Show the store's own battery option name — the workbook key is an
        // internal name the shopper never sees on the product page.
        this.setText('[data-result-battery]', this.batteryOptionName(this.batteryKey));
        this.setText('[data-result-motor]', this.displayName(build.motor));
        this.renderMotorHeight(result.motorHeight);

        const image = this.querySelector('[data-result-kit-image]');
        if (image && product.image) {
          image.src = product.image;
          image.alt = product.title || '';
        }

        if (integratedWithoutKit) {
          this.renderKitPrice(null);
          this.hide(this.kitAtc);
          const links = (this.config && this.config.links) || {};
          this.showAltLink(links.integratedMastRange || '#', 'Browse the integrated mast range');
          return;
        }

        // Buyable, or sold out. Either way the card shows the build the quiz
        // recommended — the quiz never swaps in another kit, battery or motor.
        // Sold out with a real variant still shows that variant's price; with
        // no variant at all (see kitVariantFor) there is no price to show.
        this.renderKitPrice(this.kitVariant);
        this.hide(this.kitAltLink);
        this.setKitButton(this.kitPurchasable);
      }

      // The Add To Cart button in its two states. Both labels live on the markup
      // (FOI-537 #112 pattern): the default is the button's own text, the other
      // is data-label-sold-out. [data-sold-out] scopes the grey styling so an
      // in-flight add (disabled + aria-busy, see setBusy) does not read as sold
      // out.
      setKitButton(purchasable) {
        const button = this.kitAtc;
        if (!button) return;
        button.disabled = !purchasable;
        button.textContent = purchasable ? this.kitAtcLabel : button.dataset.labelSoldOut || this.kitAtcLabel;
        if (purchasable) button.removeAttribute('data-sold-out');
        else button.setAttribute('data-sold-out', '');
        this.show(button);
      }

      // The motor height the engine returns for EVERY build. On the Integrated
      // Mast path there is no cable card, so this is the only sizing information
      // the shopper gets.
      //
      // QC #156 — the height now has its own MOTOR HEIGHT field beside Battery
      // and Motor, instead of a second line inside MOTOR. The rules file words
      // the value "20cm Motor Height", which under its own label would read
      // "MOTOR HEIGHT / 20cm Motor Height", so the repeated words come off. The
      // Email Kit body already did exactly this — see buildEmailBody().
      //
      // Still no shopper-facing English here: the wording comes from the rules
      // file, this only trims a suffix the label already carries.
      renderMotorHeight(motorHeight) {
        const label = motorHeight
          ? this.displayName(motorHeight).replace(/\s*Motor Height\s*$/i, '')
          : '';
        this.setText('[data-result-motor-height]', label);
        this.toggle(this.querySelector('[data-result-motor-height-row]'), !!label);
      }

      renderKitPrice(variant) {
        if (!variant) {
          this.clearPrice(this.kitPrice);
          this.hide(this.kitPrice);
          this.hide(this.kitCompare);
          return;
        }
        if (this.kitPrice) {
          this.setPrice(this.kitPrice, variant.price);
          this.show(this.kitPrice);
        }
        if (this.kitCompare) {
          const compare = variant.compareAtPrice || 0;
          if (compare > (variant.price || 0)) {
            this.setPrice(this.kitCompare, compare);
            this.show(this.kitCompare);
          } else {
            this.clearPrice(this.kitCompare);
            this.hide(this.kitCompare);
          }
        }
      }

      showAltLink(href, label) {
        if (!this.kitAltLink) return;
        this.kitAltLink.href = href;
        this.kitAltLink.textContent = label;
        this.show(this.kitAltLink);
      }

      showNotice(message) {
        if (!this.notice) return;
        this.notice.textContent = message || '';
        this.toggle(this.notice, !!message);
      }

      /* ------------------------------------------------------- upgrade cards */

      // Cables come from `build`, the flat view of the engine result, like every
      // other renderer here — so the cable cards and the kit card always describe
      // the same build.
      renderUpgrades(result, build) {
        this.upgradeItems = [];
        if (!this.upgradesContainer || !this.upgradeTemplate) return;
        this.upgradesContainer.textContent = '';

        if (!build.integrated) this.renderCableCards(build.cables);
        this.renderAccessoryCards(result);
      }

      renderCableCards(cables) {
        const product = this.product('cable');
        if (!product) {
          if (cables.length) console.warn('[pd-quiz] no product configured for "cable"');
          return;
        }

        const includedOrder = (this.rules && this.rules.includedInKit && this.rules.includedInKit.cable) || null;

        cables.forEach((cable, index) => {
          const variant = this.findCableVariant(product, cable.order);
          const notes = [];
          // The included size ships in the box no matter how many cables the
          // quiz recommends — never sell it again (workbook tab 10).
          const included = cable.order === includedOrder;

          if (included) notes.push('Included with every kit');
          if (cable.order !== cable.effective) notes.push('Sized one step up for Fusion');
          if (index === 1) notes.push('Long cable for efoil / cruise sessions');
          // FOI-540 QC low 2. buildCard() drops the Add button for a sold-out
          // cable but still prints its price, so the card gave a price and no
          // way to buy and no reason. The accessory cards already say why.
          if (!included && variant && variant.available === false) notes.push(this.unavailableNote);

          const card = this.buildCard({
            title: `Removable v2 Motor Cable – ${cable.order}cm`,
            image: product.image,
            note: notes.join(' — '),
            variant: included ? null : variant,
            url: product.url,
            included,
          });
          if (card) card.setAttribute('data-standard-only', '');
        });
      }

      // The position of a named option on a product, or -1 when the product does
      // not carry it. Never assume a position: FOI-540 QC low 3 — "Cable Length"
      // is option 1 today, and one option added in front of it would silently
      // repoint every cable card.
      optionIndex(product, name) {
        const options = (product && product.options) || [];
        const wanted = String(name == null ? '' : name).trim().toLowerCase();
        if (!wanted) return -1;
        for (let i = 0; i < options.length; i++) {
          const option = String(options[i] == null ? '' : options[i]).trim().toLowerCase();
          if (option === wanted) return i;
        }
        return -1;
      }

      findCableVariant(product, order) {
        const names = (this.config && this.config.optionNames) || {};
        const prefix = (names.cablePrefix || {})[order];
        if (!prefix) return null;
        const wanted = String(prefix).trim();
        // Match on the LEADING NUMBER, never the whole value: the AU sizes mix
        // one straight double-quote (12cm / 5") with two single-quotes
        // (15cm / 6''), which look identical and are different bytes.
        const index = this.optionIndex(product, names.cableOption);
        const matches = (product.variants || []).filter((variant) => {
          const values = this.optionValues(variant);
          // Option name not found (renamed, or another store's catalogue): read
          // every option value rather than guessing a position. Each prefix is
          // unique across the seven sizes, so a false match needs a second
          // option whose value also starts "20cm".
          const candidates = index === -1 ? values : [values[index] || ''];
          return candidates.some((value) => value.indexOf(wanted) === 0);
        });
        return matches.filter((variant) => variant.available !== false)[0] || matches[0] || null;
      }

      // One wording for a sold-out upgrade card, used by both card types. The
      // same word the kit button uses, so one state has one name.
      get unavailableNote() {
        return 'Sold out';
      }

      renderAccessoryCards(result) {
        (result.accessories || []).forEach((key) => {
          const product = this.product(key);
          if (!product) {
            console.warn(`[pd-quiz] no product configured for accessory "${key}"`);
            return;
          }
          const variant = (product.variants || [])[0] || null;
          this.buildCard({
            title: product.title || this.displayName(key),
            image: product.image,
            note: variant && variant.available === false ? this.unavailableNote : '',
            variant,
            url: product.url,
            included: false,
          });
        });
      }

      // renderIncludedCards() lived here. The v2.2 engine no longer returns
      // `includedAccessories` — the blade hubs it listed were removed from the
      // product model — so it rendered nothing and promised a field that is gone.

      buildCard(data) {
        const source = this.upgradeTemplate.content.firstElementChild;
        if (!source) return null;
        const card = source.cloneNode(true);

        const image = card.querySelector('[data-upgrade-image]');
        if (image && data.image) {
          image.src = data.image;
          image.alt = data.title || '';
        }

        const title = card.querySelector('[data-upgrade-title]');
        if (title) title.textContent = data.title || '';

        const note = card.querySelector('[data-upgrade-note]');
        if (note) {
          note.textContent = data.note || '';
          this.toggle(note, !!data.note);
        }

        const sellable = !data.included && !!data.variant && data.variant.available !== false;
        const index = this.upgradeItems.length;
        this.upgradeItems.push({
          title: data.title || '',
          url: data.url || '',
          included: !!data.included,
          sellable,
          id: data.variant ? data.variant.id : null,
          price: data.variant ? data.variant.price || 0 : 0,
          compareAtPrice: data.variant ? data.variant.compareAtPrice || 0 : 0,
        });

        const price = card.querySelector('[data-upgrade-price]');
        if (price) {
          if (data.included || !data.variant) {
            this.clearPrice(price);
            this.hide(price);
          } else {
            this.setPrice(price, data.variant.price);
            this.show(price);
          }
        }

        const button = card.querySelector('[data-upgrade-atc]');
        if (button) {
          if (sellable) {
            button.dataset.upgradeIndex = String(index);
            // The visible label is only "Add" (design 7437:23162), and a results
            // panel can carry six of them. Name the product so a screen-reader
            // user can tell the buttons apart.
            if (data.title) button.setAttribute('aria-label', `Add ${data.title} to cart`);
            this.show(button);
          } else {
            button.remove();
          }
        }

        this.upgradesContainer.appendChild(card);
        return card;
      }

      /* -------------------------------------------------------------- totals */

      updateTotals() {
        const totalEl = this.querySelector('[data-result-total]');
        const compareEl = this.querySelector('[data-result-total-compare]');

        let total = 0;
        let compare = 0;
        if (this.kitPurchasable && this.kitVariant) {
          total += this.kitVariant.price || 0;
          compare += this.kitVariant.compareAtPrice || this.kitVariant.price || 0;
        }
        this.upgradeItems.forEach((item) => {
          if (!item.sellable) return;
          total += item.price;
          compare += item.compareAtPrice || item.price;
        });

        if (totalEl) this.setPrice(totalEl, total);
        if (compareEl) {
          if (compare > total) {
            this.setPrice(compareEl, compare);
            this.show(compareEl);
          } else {
            this.clearPrice(compareEl);
            this.hide(compareEl);
          }
        }

        // "Add All" that quietly skips the kit is misleading — hide it and leave
        // the per-item buttons instead.
        const sellableCount = this.upgradeItems.filter((item) => item.sellable).length;
        const nothingPurchasable = !this.kitPurchasable && sellableCount === 0;

        // A new recommendation (or a switched AC lead) means a different set of
        // variants, so the "Added To Cart" latch no longer applies. Never release
        // it mid-request — an in-flight add still owns the button.
        if (this.addAllButton && !this.addAllButton.hasAttribute('aria-busy')) {
          this.setAddAllAdded(false);
        }

        this.toggle(this.addAllButton, this.kitPurchasable && !nothingPurchasable);

        // Last price write in both paths that produce results — renderResults()
        // and the AC-lead change — so this is the one place the tax pass needs
        // to be told the prices are ready.
        this.notifyPricesRendered();
      }

      /* --------------------------------------------------------------- links */

      updateLinks(build) {
        if (this.exploreLink && this.kitProduct && this.kitProduct.url) {
          this.exploreLink.href = this.kitProduct.url;
        }

        const query = this.buildMailtoQuery(build);
        if (this.emailKitLink) this.emailKitLink.href = this.emailKitBase + query;
        this.adviceMailtoLinks.forEach((link, index) => {
          link.href = this.adviceMailtoBases[index] + query;
        });
      }

      buildMailtoQuery(build) {
        const origin = window.location.origin;
        const lines = [];
        const motorHeight = this.result ? this.result.motorHeight : '';

        if (this.kitProduct) {
          lines.push(this.kitProduct.title || this.displayName(build.system));
          // FOI-540 QC low 4. The email has to say what the card says. The card
          // shows the store's own battery option name ("FM+ 650"), so the email
          // must too — displayName() here wrote "FM+ 650 Battery", a name the
          // shopper never sees on the product page. The height was a bare line
          // with no label, unlike the two above it; the rules file words it
          // "20cm Motor Height", so the label drops the repeated words.
          lines.push(`Battery: ${this.batteryOptionName(this.batteryKey)}`);
          lines.push(`Motor: ${this.displayName(build.motor)}`);
          if (motorHeight) {
            lines.push(`Motor height: ${this.displayName(motorHeight).replace(/\s*Motor Height\s*$/i, '')}`);
          }
        }
        this.upgradeItems.forEach((item) => {
          lines.push(item.included ? `${item.title} (included)` : item.title);
        });
        lines.push('');
        if (this.kitProduct && this.kitProduct.url) lines.push(origin + this.kitProduct.url);
        this.upgradeItems.forEach((item) => {
          if (item.url) lines.push(origin + item.url);
        });

        const body = lines.map((line) => encodeURIComponent(line)).join('%0D%0A');
        return `?subject=${encodeURIComponent(this.emailSubject)}&body=${body}`;
      }

      /* ---------------------------------------------------------------- cart */

      addKitToCart(button) {
        if (!this.kitPurchasable || !this.kitVariant) return;
        this.addToCart([{ id: this.kitVariant.id, price: this.kitVariant.price || 0 }], 'single', button);
      }

      addUpgradeToCart(button) {
        const item = this.upgradeItems[parseInt(button.dataset.upgradeIndex, 10)];
        if (!item || !item.sellable) return;
        this.addToCart([{ id: item.id, price: item.price }], 'single', button);
      }

      addAllToCart() {
        const items = [];
        if (this.kitPurchasable && this.kitVariant) {
          items.push({ id: this.kitVariant.id, price: this.kitVariant.price || 0 });
        }
        this.upgradeItems.forEach((item) => {
          if (item.sellable) items.push({ id: item.id, price: item.price });
        });
        if (!items.length) return;
        this.addToCart(items, 'all', this.addAllButton);
      }

      setBusy(button, busy) {
        if (!button) return;
        button.disabled = busy;
        if (busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }

      // The kit is an ~$8k line item, so "Add All" must not re-arm itself after a
      // successful add — a second press adds the same variants again and Shopify
      // merges them into qty 2. Latch it until the recommendation actually
      // changes; updateTotals() owns the release.
      setAddAllAdded(added) {
        const button = this.addAllButton;
        if (!button) return;
        button.disabled = added;
        button.removeAttribute('aria-busy');
        button.textContent = added ? 'Added To Cart' : this.addAllLabel;
      }

      addToCart(items, mode, button) {
        const routes = window.routes || {};
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };

        const body = { items: items.map((item) => ({ id: item.id, quantity: 1 })) };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(body);

        this.setBusy(button, true);
        let added = false;

        fetch(`${routes.cart_add_url || '/cart/add.js'}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.warn('[pd-quiz] cart add rejected', response.description || response.message);
              this.showNotice('Could not add to cart — please try again.');
              return;
            }

            added = true;

            this.track({
              event: 'quiz_add_to_cart',
              quiz_mode: mode,
              quiz_item_ids: items.map((item) => item.id),
              quiz_value: items.reduce((sum, item) => sum + (item.price || 0), 0),
            });

            if (!cart) {
              window.location = routes.cart_url || '/cart';
              return;
            }
            // The add has already succeeded past this point. Neither a
            // throwing cartUpdate subscriber (publish has no internal
            // try/catch) nor a failed drawer re-render (Shopify returns null
            // section HTML when the bundled section render is throttled) may
            // surface as a false "could not add to cart" error.
            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pd-quiz', cartData: response });
              }
            } catch (err) {
              console.warn('[pd-quiz] cartUpdate subscriber threw', err);
            }
            try {
              cart.renderContents(response);
            } catch (err) {
              console.warn('[pd-quiz] cart re-render failed after a successful add', err);
            }
          })
          .catch((err) => {
            console.warn('[pd-quiz] cart add failed', err);
            this.showNotice('Could not add to cart — please try again.');
          })
          .finally(() => {
            if (added && mode === 'all') this.setAddAllAdded(true);
            else this.setBusy(button, false);
          });
      }

      /* -------------------------------------------------------------- retake */

      retake() {
        this.clearStorage();
        this.result = null;
        this.build = null;
        this.kitProduct = null;
        this.kitVariant = null;
        this.kitPurchasable = false;
        this.upgradeItems = [];
        this.started = false;

        this.querySelectorAll('[data-quiz-input]').forEach((input) => {
          input.checked = false;
          input.disabled = false;
        });
        this.querySelectorAll('[data-quiz-next]').forEach((button) => {
          button.disabled = true;
        });
        if (this.upgradesContainer) this.upgradesContainer.textContent = '';
        if (this.notice) {
          this.notice.textContent = '';
          this.hide(this.notice);
        }

        this.hide(this.results);
        this.showStage(0, { silent: true });
        this.moveFocus(this.stages.length ? this.stages[0].querySelector('legend') : null);
        this.scrollElementIntoView(this.stages[0]);
        this.announce('Quiz restarted');
      }

      /* -------------------------------------------------- stage-2 hover video */

      canPlayHoverVideo() {
        return window.matchMedia('(min-width: 1024px)').matches && !this.prefersReducedMotion();
      }

      handleHoverIn(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        const card = target.closest('[data-hover-video]');
        if (!card || !this.canPlayHoverVideo()) return;

        let video = card.querySelector('[data-hover-video-el]');
        if (!video) {
          const mount = card.querySelector('.pd-quiz__option-media');
          if (!mount) return;
          video = document.createElement('video');
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('preload', 'none');
          video.setAttribute('data-hover-video-el', '');
          video.setAttribute('aria-hidden', 'true');
          // No stylesheet rule exists for a JS-injected video; the media box is
          // position:relative with a 1/1 ratio, so cover it directly.
          video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;';
          video.src = card.dataset.hoverVideo;
          mount.appendChild(video);
        }

        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }

      handleHoverOut(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        const card = target.closest('[data-hover-video]');
        if (!card) return;
        const video = card.querySelector('[data-hover-video-el]');
        if (video) video.pause();
      }
    }
  );
}
